import { GripVertical, Minus, Plus } from "lucide-react";
import { useState } from "react";

import type { FlowSettingView, ProjectFlowSettings } from "@/api/flowSettings";
import type { ClinicalSummaryItem } from "@/api/hooks";
import type { LibraryAsset } from "@/api/library";
import { adoptionRefusal } from "@/lib/adoptions";
import {
  maxSlotCount,
  priorityText,
  priorityTrackOf,
  stagedKey,
} from "@/lib/flowSettings";
import {
  reprioritise,
  resetTargets,
  speaksFor,
  summarySentence,
  trackRows,
  type TrackRow,
} from "@/lib/interviewTracks";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import {
  ItemActions,
  ItemBadges,
  type SectionReviewPanelContext,
} from "@/ui/review/SectionReviewList";

import { FinalQuestioningSwitchRow } from "./FinalQuestioningControl";
import type { PathwayFlowSurface } from "./PathwayFlow";

/** Which tracks an interview runs, in what order, which every patient gets, and how many in all. */
export function InterviewTracksPanel({
  surface,
  state,
  nameOfTrack,
  projectId,
  context,
  catalog,
}: {
  surface: PathwayFlowSurface;
  state: ProjectFlowSettings;
  nameOfTrack: (track: string) => string;
  projectId: string;
  context: SectionReviewPanelContext;
  /** The library catalog, for the tracks this project does not use. */
  catalog: readonly LibraryAsset[] | null;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  // `settled` too: a write offered against a reading whose refresh failed would
  // be computed from a selection the service may already have replaced.
  const blocked = !surface.canWrite || surface.saving || !surface.settled;
  // The service refuses a per-track priority beside a wholesale override, so the other rows stay put until it is reset.
  const overridden = state.trackPriorityEntries.some(
    (view) => view.state === "authored_above",
  );

  const priorityEntry = (track: string): FlowSettingView | undefined =>
    state.trackPriorityEntries.find((view) => priorityTrackOf(view) === track);
  const priorityOf = (track: string): number | null => {
    const view = priorityEntry(track);
    const text =
      surface.priorities.get(track) ?? (view ? priorityText(view) : "");
    return text.trim() === "" || !Number.isInteger(Number(text))
      ? null
      : Number(text);
  };

  const rows = trackRows(state, surface.slots, priorityOf);
  const required = rows.filter((row) => row.required);
  const others = rows.filter((row) => !row.required);
  const limit = surface.displayLimit;
  const bound = maxSlotCount(state);
  const sentence = summarySentence(
    required.map((row) => nameOfTrack(row.track)),
    limit,
  );

  function move(row: TrackRow, to: number) {
    const group = row.required ? required : others;
    const from = group.findIndex((entry) => entry.track === row.track);
    if (from === -1 || to < 0 || to >= group.length || to === from) {
      return;
    }
    setAnnouncement(
      `${nameOfTrack(row.track)} moved to position ${
        (row.required ? 0 : required.length) + to + 1
      } of ${rows.length}.`,
    );
    if (row.required) {
      surface.moveRequired(row.track, to);
      return;
    }
    const order = group.map((entry) => entry.track);
    order.splice(from, 1);
    order.splice(to, 0, row.track);
    const current = new Map(order.map((track) => [track, priorityOf(track)]));
    for (const [track, value] of reprioritise(order, current)) {
      if (value !== current.get(track)) {
        surface.editPriority(track, String(value));
      }
    }
  }

  // From the catalog, not the section rows: a project that adopted nothing still needs to add its first track.
  const held = new Set([
    ...state.adoptedPathways.map((pin) => pin.assetId),
    ...context.items
      .filter((item) => item.supplyState === "adopted")
      .map((item) => item.id),
  ]);
  const itemById = new Map(context.items.map((entry) => [entry.id, entry]));
  // The service reports no item for a pack-supplied pathway, so only an adopted track's row has a menu.
  const itemOfTrack = (track: string): ClinicalSummaryItem | undefined => {
    const pin = state.adoptedPathways.find(
      (candidate) =>
        candidate.track === track && candidate.assetType === "pathway",
    );
    return pin === undefined ? undefined : itemById.get(pin.assetId);
  };
  // An adopted pathway no track row places keeps its menu in a list of its own.
  const placed = new Set(rows.flatMap((row) => itemOfTrack(row.track)?.id ?? []));
  const otherItems = context.items.filter((entry) => !placed.has(entry.id));
  const unused = (catalog ?? []).filter(
    (asset) =>
      asset.assetType === "pathway" &&
      asset.archivedAt === null &&
      !held.has(asset.id) &&
      !rows.some((row) => speaksFor(asset.name, row.track)),
  );

  return (
    <section
      data-slot="interview-tracks"
      aria-labelledby="interview-tracks-heading"
      className="space-y-4 rounded-lg border border-border bg-card px-4 py-4"
    >
      <header className="space-y-1">
        <h3
          id="interview-tracks-heading"
          className="text-sm font-semibold text-foreground"
        >
          Interview tracks
        </h3>
        {sentence !== null ? (
          <p className="text-sm text-foreground">{sentence}</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            The Editor Service could not work out how many tracks this project
            runs: its settings declare neither selection shape completely.
          </p>
        )}
      </header>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
          No track is in use in this project yet.
          {unused.length > 0 ? " Add one from the list below." : ""}
        </p>
      ) : (
        <ol
          aria-label="Interview tracks, in the order they run"
          className="overflow-hidden rounded-lg border border-border"
        >
          {rows.map((row) => {
            const group = row.required ? required : others;
            const index = group.findIndex((entry) => entry.track === row.track);
            const name = nameOfTrack(row.track);
            const rowItem = itemOfTrack(row.track);
            const actions =
              rowItem === undefined
                ? undefined
                : context.actions?.byItemId.get(rowItem.id);
            const canReorder = !blocked && !(overridden && !row.required);
            return (
              <li
                key={row.track}
                data-track={row.track}
                draggable={canReorder}
                onDragStart={() => setDragging(row.track)}
                onDragEnd={() => setDragging(null)}
                onDragOver={(event) => {
                  const from = rows.find((entry) => entry.track === dragging);
                  if (from !== undefined && from.required === row.required) {
                    event.preventDefault();
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const from = rows.find((entry) => entry.track === dragging);
                  setDragging(null);
                  if (from !== undefined && from.required === row.required) {
                    move(from, index);
                  }
                }}
                className="group flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-card px-3 py-2.5 last:border-b-0"
              >
                <button
                  type="button"
                  aria-label={`Reorder ${name}: press the up or down arrow to move it`}
                  aria-disabled={!canReorder || undefined}
                  onKeyDown={(event) => {
                    if (!canReorder) {
                      return;
                    }
                    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                      event.preventDefault();
                      move(row, index + (event.key === "ArrowUp" ? -1 : 1));
                    }
                  }}
                  className="cursor-grab rounded text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                >
                  <GripVertical aria-hidden="true" className="h-4 w-4" />
                </button>

                <div className="min-w-0 flex-1">
                  <p
                    className={
                      "wrap-anywhere text-sm font-medium " +
                      (row.required
                        ? "text-foreground"
                        : "text-muted-foreground")
                    }
                  >
                    {name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {row.required ? "Always assessed" : "Only if classified"}
                    {" · "}
                    {sourceOf(rowItem)}
                  </p>
                </div>

                {rowItem !== undefined ? (
                  <ItemBadges item={rowItem} converging={context.converging} />
                ) : null}

                <RequiredSwitch
                  name={name}
                  on={row.required}
                  blocked={blocked}
                  onToggle={() =>
                    surface.toggleAlways(row.track, !row.required)
                  }
                />

                {/* A fixed slot, so the switches line up whether or not a row has a menu. */}
                <span className="flex h-9 min-w-9 shrink-0 items-center justify-center">
                  {rowItem !== undefined && !context.converging ? (
                    <ItemActions
                      item={rowItem}
                      actions={actions}
                      onDecide={context.actions?.onDecide}
                    />
                  ) : null}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <p role="status" className="sr-only">
        {announcement}
      </p>

      {overridden ? (
        <p className="text-xs text-muted-foreground">
          The order of the tracks that are only used if classified is stored as
          one block in this project, so the tracks below the required ones cannot
          be reordered until it is reset to the base pack.
        </p>
      ) : null}

      {otherItems.length > 0 ? (
        <ul
          aria-label="Other pathways in this project"
          className="overflow-hidden rounded-lg border border-border"
        >
          {otherItems.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-3 py-2.5 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="wrap-anywhere text-sm font-medium text-foreground">
                  {entry.name}
                </p>
                <p className="text-xs text-muted-foreground">{sourceOf(entry)}</p>
              </div>
              <ItemBadges item={entry} converging={context.converging} />
              {context.converging ? null : (
                <ItemActions
                  item={entry}
                  actions={context.actions?.byItemId.get(entry.id)}
                  onDecide={context.actions?.onDecide}
                />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          id="interview-tracks-limit-label"
          className="text-sm font-medium text-foreground"
        >
          Tracks per interview
        </span>
        <span
          role="group"
          aria-labelledby="interview-tracks-limit-label"
          className="flex items-center gap-1"
        >
          <IconButton
            label="Fewer tracks per interview"
            variant="outline"
            icon={<Minus className="h-4 w-4" />}
            disabled={blocked || limit === null || limit <= 1}
            onClick={() => limit !== null && surface.resize(limit - 1)}
          />
          <output
            aria-label="Tracks per interview"
            className="w-8 text-center text-sm font-medium text-foreground"
          >
            {limit ?? "–"}
          </output>
          <IconButton
            label="More tracks per interview"
            variant="outline"
            icon={<Plus className="h-4 w-4" />}
            disabled={blocked || limit === null || limit >= bound}
            onClick={() => limit !== null && surface.resize(limit + 1)}
          />
        </span>
        <span className="text-xs text-muted-foreground">
          Required tracks count toward this number. This pack can fill at most{" "}
          {bound}.
        </span>
      </div>

      {unused.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Not used in this project
          </h4>
          <ul className="flex flex-wrap gap-2">
            {unused.map((asset) => {
              // The same refusals the section rows' Adopt states.
              const refusal = adoptionRefusal("pin", {
                asset,
                lock: null,
                held: false,
                missingWriteReason: surface.writeReason,
                policyUnread: context.policyUnread,
              });
              return (
                <li key={asset.id}>
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`Add ${asset.name} to this project`}
                    disabled={context.actions === undefined || refusal !== null}
                    title={refusal ?? undefined}
                    onClick={() =>
                      context.actions?.onDecide({
                        mode: "pin",
                        assetId: asset.id,
                        version: asset.latestVersion,
                      })
                    }
                  >
                    <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                    {asset.name}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <div className="border-t border-border pt-3">
        <FinalQuestioningSwitchRow projectId={projectId} />
      </div>

      <p className="text-xs text-muted-foreground">
        These settings win over the same keys edited by hand in
        flow_config.yaml.
      </p>

      <ResetAndDetails
        surface={surface}
        state={state}
        nameOfTrack={nameOfTrack}
      />
    </section>
  );
}

function sourceOf(item: ClinicalSummaryItem | undefined): string {
  if (item === undefined || item.adoptedVersion === null) {
    return "From the pack";
  }
  return item.provenance === "overridden"
    ? `${item.adoptedVersion} from library · overridden in this project`
    : `${item.adoptedVersion} from library`;
}

function RequiredSwitch({
  name,
  on,
  blocked,
  onToggle,
}: {
  name: string;
  on: boolean;
  blocked: boolean;
  onToggle: () => void;
}) {
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      <span aria-hidden="true">Required</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-disabled={blocked || undefined}
        aria-label={`${name} is required`}
        onClick={() => {
          if (!blocked) {
            onToggle();
          }
        }}
        className={
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-input bg-secondary transition-colors " +
          "aria-checked:border-primary aria-checked:bg-primary " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring " +
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        }
      >
        <span
          aria-hidden="true"
          className={
            "pointer-events-none ml-0.5 h-3.5 w-3.5 rounded-full bg-card shadow transition-transform " +
            (on ? "translate-x-4" : "translate-x-0")
          }
        />
      </button>
    </span>
  );
}

/** Resets every changed setting together: restoring the slot list alone leaves neither selection shape declared. */
function ResetAndDetails({
  surface,
  state,
  nameOfTrack,
}: {
  surface: PathwayFlowSurface;
  state: ProjectFlowSettings;
  nameOfTrack: (track: string) => string;
}) {
  const targets = resetTargets(state, nameOfTrack);
  const isStaged = (target: (typeof targets)[number]) =>
    surface.staged.get(stagedKey(target.setting, target.track))?.op === "reset";
  const allStaged = targets.length > 0 && targets.every(isStaged);
  const blocked = !surface.canWrite || surface.saving || !surface.settled;
  const reset =
    targets.length === 0 ? null : (
      <Button
        size="sm"
        variant="link"
        disabled={blocked}
        aria-pressed={allStaged}
        onClick={() => {
          for (const target of targets) {
            if (allStaged || !isStaged(target)) {
              surface.stage({ op: "reset", ...target });
            }
          }
        }}
      >
        {allStaged ? "Undo reset to base pack" : "Reset to base pack"}
      </Button>
    );
  if (state.findings.length === 0) {
    return reset;
  }
  return (
    <details
      data-slot="pathway-routing"
      className="rounded-md border border-border"
    >
      <summary
        aria-label="More about these settings"
        className="cursor-pointer px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        More about these settings
      </summary>
      <div className="space-y-3 border-t border-border px-3 py-3">
        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            Why this would not load
          </h4>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {state.findings.map((finding) => (
              <li key={`${finding.code}:${finding.path}`}>
                <span className="text-foreground">{finding.message}</span>
                {(finding.subjects ?? []).length > 0
                  ? ` (${(finding.subjects ?? []).join(", ")})`
                  : ""}
              </li>
            ))}
          </ul>
        </section>
        {reset}
      </div>
    </details>
  );
}
